import React, { useState, useEffect, useRef } from 'react';
import DOMPurify from 'dompurify';
import './../styles/Main.css';
import './../styles/ChromePage.css';
import { useAuth } from './../context/AuthContext';
import getBaseUrl from './../utils/baseUrl';
import { useNavigate, useParams } from 'react-router-dom';
import useSearchHighlight from './hooks/useSearchHighlight';
import VideoModal from './modals/VideoModal';

function ChromePage() {
  const [content, setContent] = useState('');
  const [videoUrl, setVideoUrl] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { chromeUrl } = useAuth();
  const contentRef = useRef(null);
  const navigate = useNavigate();
  const { documentName } = useParams();

  useEffect(() => {
    const loadCombinedContent = async () => {
      try {
        const docs = ['Chrome1.html', 'Chrome2.html'];
        if (documentName) {
          fetchContent(documentName);
        } else {
          const html1 = await fetchContent(docs[0]);
          const html2 = await fetchContent(docs[1]);
          const combinedContent = `
            ${html1}
            <div class="dynamic-data">
              <h3>${chromeUrl || 'No URL available'}</h3>
            </div>
            ${html2}
          `;
          setContent(combinedContent);
          setTimeout(() => addLinkClickListener(), 100);
        }
      } catch (error) {
        console.error('Error loading content:', error);
        setContent('<p>Error loading content. Please try again later.</p>');
      }
    };

    
      loadCombinedContent();
   
  }, [documentName, chromeUrl]);

  const fetchContent = async (fileName) => {
    try {
      const response = await fetch(`${getBaseUrl()}/guides/chrome/${fileName}`);
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      let html = await response.text();

      // Fix image paths to use absolute URLs
      const baseUrl = getBaseUrl();
      html = html.replace(/src="(?:\.\/)?images\//gi, `src="${baseUrl}/guides/chrome/images/`);

      return DOMPurify.sanitize(html);
    } catch (error) {
      console.error('Error fetching the HTML file:', error);
      return '<p>Error loading content. Please try again later.</p>';
    }
  };

  const addLinkClickListener = () => {
    const links = contentRef.current.querySelectorAll('a');
    links.forEach(link => {
      link.removeEventListener('click', handleLinkClick);
      link.addEventListener('click', handleLinkClick);
    });
  };

  const handleLinkClick = (event) => {
    event.preventDefault();
    const href = event.currentTarget.getAttribute('href');

    if (href && (href.endsWith('.mp4') || href.endsWith('.mov') || href.endsWith('.webm'))) {
      const videoPath = href.startsWith('/') ? href : `${getBaseUrl()}/${href}`;
      setVideoUrl(videoPath);
      setIsModalOpen(true);
    } else if (href && href.startsWith('guides/chrome/')) {
      const documentName = href.split('/').pop();
      navigate(`/chrome/${documentName}`);
    } else {
      window.location.href = href;
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setVideoUrl(null);
  };

  const { searchTerm, handleSearch, highlightedContent } = useSearchHighlight(content);

  return (
    <div className="chrome-page-layout">
      <div className="chrome-header">
        <p className="chrome-title"></p>
        <div className="search-input-button-container">
          <div className="search-bar">
            <input 
              type="text" 
              className="search-input" 
              placeholder="Search..." 
              value={searchTerm}
              onChange={handleSearch}
            />
          </div>
          <button className="search-button" title="Clear" onClick={() => handleSearch({ target: { value: '' } })}>Clear</button>
        </div>
      </div>
      <div className="chrome-content">
        <div className="chrome-html-content">
          <div className="chrome-title-box">
            <h1 className="chrome-title-text">Install eSafe for Chrome</h1>
          </div>
          <div ref={contentRef} dangerouslySetInnerHTML={{ __html: highlightedContent }} />
        </div>
      </div>
      {isModalOpen && (
        <VideoModal videoUrl={videoUrl} isOpen={isModalOpen} onClose={closeModal} />
      )}
    </div>
  );
}

export default ChromePage;
